import { PublicInfoPage } from "@/pages/PublicInfoPage";

const questionsStructuredData = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "What is Liminl?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Liminl is a private tool for navigating your career through the people you already know. It maps the human paths between where you are and the roles you want, then helps you act on them. Think of it as relationship intelligence for your next move, built on the idea that a warm introduction still beats a cold application.",
      },
    },
    {
      "@type": "Question",
      name: "Who is Liminl for?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "People who are serious about their next step and understand that careers move through relationships. That includes those who are actively looking, quietly open, preparing to pivot, or getting ready for a bigger leap. It is built for anyone who would rather reach the right person through a trusted path than send another application into a void.",
      },
    },
    {
      "@type": "Question",
      name: "How does it work?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Three things work together. Path mapping surfaces the real human bridges from you to the roles you want. Personal agents figure out the next move, get you ready, and draft outreach in your voice so momentum never stalls. The network multiplier lets you and the people you trust overlap your networks privately, revealing warm paths hidden one or two connections away.",
      },
    },
    {
      "@type": "Question",
      name: "Why warm introductions?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "As AI makes it effortless to auto-apply and mass-message, genuine intention gets lost and everyone competes in the same crowded, cold channel. A real introduction carries trust that a cold message cannot. Navigating your career through an authentic network is not only the most effective way to land a role, it tends to lead to a more considered and fulfilling path.",
      },
    },
    {
      "@type": "Question",
      name: "Is Liminl private?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes, privacy is the default. Your map is yours. We do not sell your data, nothing is sent on your behalf until you approve it, and your network is never exposed to other members without your explicit action. Contacts are only ever surfaced to help you, not to be traded.",
      },
    },
    {
      "@type": "Question",
      name: "How do invites work?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Liminl is invite-only during private beta. An invite from someone already inside opens the door, and each member has a limited number to share. Inviting people you trust makes the map denser and the paths warmer for everyone, which is why access grows through real relationships rather than open signup.",
      },
    },
    {
      "@type": "Question",
      name: "What makes it different?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Most tools optimize for volume: more listings, more applications, more automated messages. Liminl optimizes for the opposite. It treats human connection as the rare and valuable asset it is becoming as routine work is automated, and it puts that connection at the center of your search rather than burying it under noise.",
      },
    },
    {
      "@type": "Question",
      name: "How do I get access?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "If you have an invite, enter your code to get started. If you do not, you can join the waitlist or ask someone already on Liminl to send you an intro. As we grow we will expand access.",
      },
    },
  ],
};

export function QuestionsPage() {
  return (
    <PublicInfoPage
      canonicalPath="/questions"
      description="Answers about Liminl, relationship intelligence, warm introductions, private career networking, invitations, and access."
      documentTitle="Liminl Questions | Career Networking and Warm Introductions"
      headingId="questions-title"
      navItems={[
        { href: "#what-is-liminl", label: "What is Liminl?" },
        { href: "#who-is-liminl-for", label: "Who is Liminl for?" },
        { href: "#how-does-it-work", label: "How does it work?" },
        { href: "#why-warm-introductions", label: "Why warm introductions?" },
        { href: "#is-liminl-private", label: "Is Liminl private?" },
        { href: "#how-invites-work", label: "How do invites work?" },
        { href: "#what-makes-it-different", label: "What makes it different?" },
        { href: "#how-to-get-access", label: "How do I get access?" },
      ]}
      pageMeta="Last updated 4 July 2026"
      structuredData={questionsStructuredData}
      title="Questions"
    >
      <section id="what-is-liminl">
        <h2>What is Liminl?</h2>
        <p>
          Liminl is a private tool for navigating your career through the people you already know.
          It maps the human paths between where you are and the roles you want, then helps you act
          on them. Think of it as relationship intelligence for your next move, built on the idea
          that a warm introduction still beats a cold application.
        </p>
      </section>

      <section id="who-is-liminl-for">
        <h2>Who is Liminl for?</h2>
        <p>
          People who are serious about their next step and understand that careers move through
          relationships. That includes those who are actively looking, quietly open, preparing to
          pivot, or getting ready for a bigger leap. It is built for anyone who would rather reach
          the right person through a trusted path than send another application into a void.
        </p>
      </section>

      <section id="how-does-it-work">
        <h2>How does it work?</h2>
        <p>
          Three things work together. Path mapping surfaces the real human bridges from you to the
          roles you want. Personal agents figure out the next move, get you ready, and draft
          outreach in your voice so momentum never stalls. The network multiplier lets you and the
          people you trust overlap your networks privately, revealing warm paths hidden one or two
          connections away.
        </p>
      </section>

      <section id="why-warm-introductions">
        <h2>Why warm introductions?</h2>
        <p>
          As AI makes it effortless to auto-apply and mass-message, genuine intention gets lost and
          everyone competes in the same crowded, cold channel. A real introduction carries trust
          that a cold message cannot. Navigating your career through an authentic network is not
          only the most effective way to land a role, it tends to lead to a more considered and
          fulfilling path.
        </p>
      </section>

      <section id="is-liminl-private">
        <h2>Is Liminl private?</h2>
        <p>
          Yes, privacy is the default. Your map is yours. We do not sell your data, nothing is sent
          on your behalf until you approve it, and your network is never exposed to other members
          without your explicit action. Contacts are only ever surfaced to help you, not to be
          traded.
        </p>
      </section>

      <section id="how-invites-work">
        <h2>How do invites work?</h2>
        <p>
          Liminl is invite-only during private beta. An invite from someone already inside opens
          the door, and each member has a limited number to share. Inviting people you trust makes
          the map denser and the paths warmer for everyone, which is why access grows through real
          relationships rather than open signup.
        </p>
      </section>

      <section id="what-makes-it-different">
        <h2>What makes it different?</h2>
        <p>
          Most tools optimize for volume: more listings, more applications, more automated
          messages. Liminl optimizes for the opposite. It treats human connection as the rare and
          valuable asset it is becoming as routine work is automated, and it puts that connection
          at the center of your search rather than burying it under noise.
        </p>
      </section>

      <section id="how-to-get-access">
        <h2>How do I get access?</h2>
        <p>
          If you have an invite, enter your code to get started. If you do not, you can join the
          waitlist or ask someone already on Liminl to send you an intro. As we grow we will expand
          access.
        </p>
      </section>
    </PublicInfoPage>
  );
}
